import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';

export interface Notification {
  id: string;
  type:
    | 'task_created'
    | 'task_updated'
    | 'task_assigned'
    | 'task_approved'
    | 'task_disapproved'
    | 'task_dropped'
    | 'task_pending_approval'
    | 'task_completed_by_assignee'
    | 'help_requested'
    | 'help_request_created'
    | 'help_request_updated'
    | 'comment_added'
    | 'task_status_changed';
  title: string;
  message: string;
  timestamp: Date;
  read: boolean;
  taskId?: string;
  userId?: string;
}

interface NotificationContextType {
  notifications: Notification[];
  unreadCount: number;
  addNotification: (
    notification: Omit<Notification, 'id' | 'timestamp' | 'read'>
  ) => void;
  markAsRead: (notificationId: string) => void;
  markAllAsRead: () => void;
  clearAllNotifications: () => void;
  removeNotification: (notificationId: string) => void;
  removeNotificationsForTask: (taskId: string) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(
  undefined
);

export function NotificationProvider({ children }: { children: ReactNode }) {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  // Load notifications from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem('staffboard_notifications');
      if (stored) {
        const parsed = JSON.parse(stored);
        // Convert timestamp strings back to Date objects
        const notificationsWithDates = parsed.map((n: any) => ({
          ...n,
          timestamp: new Date(n.timestamp),
        }));
        setNotifications(notificationsWithDates);
      }
    } catch (error) {
      console.error('Error loading notifications from localStorage:', error);
    }
  }, []);

  // Save notifications to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem(
        'staffboard_notifications',
        JSON.stringify(notifications)
      );
    } catch (error) {
      console.error('Error saving notifications to localStorage:', error);
    }
  }, [notifications]);

  const unreadCount = notifications.filter(n => !n.read).length;

  const addNotification = (
    notification: Omit<Notification, 'id' | 'timestamp' | 'read'>
  ) => {
    const newNotification: Notification = {
      ...notification,
      id: `notification_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date(),
      read: false,
    };

    setNotifications(prev => {
      // Check for duplicate notifications (same type, taskId, and recent timestamp)
      const isDuplicate = prev.some(
        existing =>
          existing.type === newNotification.type &&
          existing.taskId === newNotification.taskId &&
          existing.title === newNotification.title &&
          Math.abs(
            existing.timestamp.getTime() - newNotification.timestamp.getTime()
          ) < 5000 // Within 5 seconds
      );

      if (isDuplicate) {
        console.log('Duplicate notification prevented:', newNotification);
        return prev;
      }

      return [newNotification, ...prev];
    });
  };

  const markAsRead = (notificationId: string) => {
    setNotifications(prev =>
      prev.map(n => (n.id === notificationId ? { ...n, read: true } : n))
    );
  };

  const markAllAsRead = () => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  };

  const clearAllNotifications = () => {
    setNotifications([]);
  };

  const removeNotification = (notificationId: string) => {
    setNotifications(prev => prev.filter(n => n.id !== notificationId));
  };

  const removeNotificationsForTask = (taskId: string) => {
    setNotifications(prev => prev.filter(n => n.taskId !== taskId));
  };

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        addNotification,
        markAsRead,
        markAllAsRead,
        clearAllNotifications,
        removeNotification,
        removeNotificationsForTask,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error(
      'useNotifications must be used within a NotificationProvider'
    );
  }
  return context;
}
